import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { KpiCard } from '../components/KpiCard';
import { Pill } from '../components/Pill';
import {
  getDashboardKpis,
  getMe,
  getRefundCheckinSummary,
  getRevenueTrend,
  getSalesBreakdown,
  type DashboardKpis,
} from '../lib/api-client';

/** 近 N 日（含今日）之 ISO 日界 [from, to]。 */
function recentRange(days: number): { from: string; to: string } {
  const to = new Date().toISOString().slice(0, 10);
  const d = new Date(`${to}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - (days - 1));
  return { from: d.toISOString().slice(0, 10), to };
}

/**
 * 營運總覽 — 版型對齊 docs/cms-screens/03-A3-dashboard.png。
 *
 * KPI 串 GET /admin/dashboard/kpis（每 30 秒重抓）；BE 一次 query 聚合 4 個指標。
 */

function formatTwd(amount: string): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return `NT$ ${Math.round(n).toLocaleString('en-US')}`;
}

function formatTimeOf(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function buildKpiCards(kpis: DashboardKpis | undefined): { label: string; value: string }[] {
  if (!kpis) {
    return [
      { label: '今日銷售額', value: '—' },
      { label: '今日訂單數', value: '—' },
      { label: '購票機在線', value: '—' },
      { label: '未 ack P1', value: '—' },
    ];
  }
  return [
    { label: '今日銷售額', value: formatTwd(kpis.today_sales) },
    { label: '今日訂單數', value: String(kpis.today_orders) },
    { label: '購票機在線', value: String(kpis.devices_online) },
    { label: '未 ack P1', value: String(kpis.unack_p1) },
  ];
}

export function DashboardPage(): JSX.Element {
  const { data: me, isLoading, error } = useQuery({ queryKey: ['auth.me'], queryFn: getMe });
  const kpiQuery = useQuery({
    queryKey: ['dashboard.kpis'],
    queryFn: getDashboardKpis,
    refetchInterval: 30_000,
  });
  const cards = buildKpiCards(kpiQuery.data);
  const trend = kpiQuery.data
    ? `截至 ${formatTimeOf(kpiQuery.data.as_of)}`
    : kpiQuery.isLoading
      ? '載入中…'
      : '取數失敗';

  // 近 7 日營收概覽（接 /admin/reports）
  const range = recentRange(7);
  const revenueQuery = useQuery({
    queryKey: ['dashboard.revenue7', range.from, range.to],
    queryFn: () => getRevenueTrend({ ...range, granularity: 'day' }),
    refetchInterval: 60_000,
  });
  const topProductsQuery = useQuery({
    queryKey: ['dashboard.topProducts7', range.from, range.to],
    queryFn: () => getSalesBreakdown({ ...range, dimension: 'product' }),
    refetchInterval: 60_000,
  });
  const refundQuery = useQuery({
    queryKey: ['dashboard.refund7', range.from, range.to],
    queryFn: () => getRefundCheckinSummary(range),
    refetchInterval: 60_000,
  });
  const revRows = revenueQuery.data?.items ?? [];
  const maxNet = Math.max(1, ...revRows.map((r) => Number(r.net_sales)));
  const net7 = revRows.reduce((s, r) => s + Number(r.net_sales), 0);
  const topProducts = (topProductsQuery.data?.items ?? []).slice(0, 5);
  const refund = refundQuery.data;

  return (
    <div>
      <p style={{ color: 'var(--c-text-muted)', margin: '0 0 20px' }}>
        歡迎使用 Boat Ticket CMS 管理介面。
      </p>

      {kpiQuery.error && (
        <div className="error-banner" style={{ marginBottom: 16 }}>
          KPI 取數失敗：{(kpiQuery.error as Error).message}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          marginBottom: 24,
        }}
      >
        {cards.map((k) => (
          <KpiCard key={k.label} label={k.label} value={k.value} trend={trend} />
        ))}
      </div>

      <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>
            近 7 日營收概覽
            <span style={{ fontSize: 13, color: 'var(--c-text-muted)', marginLeft: 10 }}>
              淨營收 {formatTwd(String(net7))}
            </span>
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {refund && (
              <div
                style={{
                  border: '1px solid var(--c-border)',
                  borderRadius: 8,
                  padding: '6px 14px',
                  textAlign: 'right',
                  lineHeight: 1.25,
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>近 7 日退票率</div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: refund.refund_rate >= 0.1 ? '#C2410C' : undefined,
                  }}
                >
                  {(refund.refund_rate * 100).toFixed(1)}%
                </div>
                <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                  {refund.refund_count} / {refund.tickets_issued} 票
                </div>
              </div>
            )}
            <Link to="/reports" style={{ fontSize: 13 }}>
              查看完整報表 →
            </Link>
          </div>
        </div>
        {revenueQuery.error && (
          <div className="error-banner">{(revenueQuery.error as Error).message}</div>
        )}
        {!revenueQuery.error && revRows.length === 0 && (
          <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>
            {revenueQuery.isLoading ? '載入中…' : '近 7 日無營收資料'}
          </div>
        )}
        {revRows.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 24 }}>
            {/* 迷你營收長條圖（net） */}
            <div>
              <svg
                viewBox={`0 0 ${Math.max(revRows.length * 52, 260)} 130`}
                style={{ width: '100%', height: 130 }}
              >
                {revRows.map((r, i) => {
                  const h = (Number(r.net_sales) / maxNet) * 96;
                  return (
                    <g key={r.period} transform={`translate(${i * 52 + 8}, 0)`}>
                      <rect x={6} y={104 - h} width={30} height={h} rx={3} fill="#0EA5A4" />
                      <text x={21} y={102 - h} fontSize={8} textAnchor="middle" fill="#334155">
                        {Math.round(Number(r.net_sales) / 1000)}k
                      </text>
                      <text x={21} y={120} fontSize={8} textAnchor="middle" fill="#64748B">
                        {r.period.slice(5)}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
            {/* 熱門商品 Top */}
            <div>
              <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginBottom: 6 }}>
                熱門商品（依營收）
              </div>
              {topProducts.length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>—</div>
              )}
              {topProducts.map((p) => (
                <div key={p.key} style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 13,
                      marginBottom: 2,
                    }}
                  >
                    <span
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {p.label}
                    </span>
                    <span style={{ color: 'var(--c-text-muted)', marginLeft: 8 }}>
                      {formatTwd(p.gross_sales)}
                    </span>
                  </div>
                  <div style={{ background: '#E2E8F0', borderRadius: 4, height: 8 }}>
                    <div
                      style={{
                        width: `${(p.share * 100).toFixed(1)}%`,
                        background: '#0EA5A4',
                        height: 8,
                        borderRadius: 4,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px' }}>當前操作者</h3>
        {isLoading && <div>載入中…</div>}
        {error && (
          <div className="error-banner">取得使用者資訊失敗：{(error as Error).message}</div>
        )}
        {me && (
          <table style={{ maxWidth: 620 }}>
            <tbody>
              <tr>
                <th style={{ width: 140 }}>使用者 ID</th>
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{me.user_id}</td>
              </tr>
              <tr>
                <th>Email</th>
                <td>{me.email}</td>
              </tr>
              <tr>
                <th>顯示名稱</th>
                <td>{me.display_name ?? '—'}</td>
              </tr>
              <tr>
                <th>權限</th>
                <td>
                  {me.permissions.length === 0 ? (
                    <Pill tone="danger">無</Pill>
                  ) : (
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
                      {me.permissions.map((p) => (
                        <Pill key={p} dot={false}>
                          {p}
                        </Pill>
                      ))}
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <th>2FA</th>
                <td>
                  {me.requires_2fa ? (
                    <Pill tone="success">已啟用</Pill>
                  ) : (
                    <Pill tone="warning">未啟用</Pill>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ padding: '18px 20px' }}>
        <h3 style={{ margin: '0 0 12px' }}>已串接之 BE 端點</h3>
        <ul style={{ lineHeight: 1.9, margin: 0, paddingLeft: 20 }}>
          <li>
            <a href="/spots">F-3 景點管理</a> — GET /spots（admin.spot）
          </li>
          <li>
            <a href="/refunds/manual-reviews">退票人工複核</a> — GET
            /admin/refunds/manual-reviews（AC-E2-6）
          </li>
        </ul>
      </div>
    </div>
  );
}
